import { t } from '@lingui/core/macro';
import {
  type TooltipPositionerCallbackFunction as DefaultPositionerCallbackFunction,
  type Point,
  type TooltipFormatterCallbackFunction,
} from 'highcharts';
import { useCallback, useEffect, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import closeIcon from '@/assets/svg/bx-x.svg';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { cn } from '@/lib/utils';

import { useFlareContext } from '../FlareContext';
import {
  type FlareChart,
  type SanitizedFlareProps,
  type TooltipContentFunction,
  type TooltipFormatFunction,
  type TooltipLinkFunction,
  type TooltipPoint,
  type TooltipPositionerCallbackFunction,
} from '../types';
import TooltipRow from './TooltipRow';
import TooltipTitle from './TooltipTitle';

const FLARE_STICKY_TOOLTIP_CONTAINER_CLASS = 'flare-sticky-tooltip-container';
const STICKY_TOOLTIP_CHART_CLASS = 'flare-sticky-tooltip-chart';

const tooltipTableClass = cn(
  'min-h-18 w-max min-w-56 border-separate border-spacing-2 rounded-sm px-3 py-2 indent-0',
  'bg-gray-900 text-white drop-shadow-xl/30',
);

type TooltipProps = {
  shared?: boolean;
  rowLabelFormat?: TooltipFormatFunction;
  rowValueFormat?: TooltipFormatFunction;
  rowValueLink?: TooltipLinkFunction;
  rowContent?: TooltipContentFunction;
  unstable_hasRouterContext?: boolean;
  rowSecondaryValueFormat?: TooltipFormatFunction;
  titleFormat?: TooltipFormatFunction;
  positioner?: TooltipPositionerCallbackFunction;
  showTotalRow?: boolean;
  reversed?: boolean;
  stickyOnClick?: boolean;
};

type SharedPoint = {
  points: TooltipPoint[];
};

const isSharedPoint = (e: SharedPoint | TooltipPoint): e is SharedPoint =>
  (e as SharedPoint).points != null;

const stickyTooltipRemove = (chart: FlareChart, showHoverTooltips = false) => {
  if (!chart?.stickyTooltip) {
    return;
  }

  chart.stickyTooltip.element.remove();
  chart.stickyTooltip = undefined;

  chart.getSelectedPoints().forEach((selectedPoint) => {
    selectedPoint.select(false, true);
    chart.getSelectedPoints().forEach((selectedPoint) => {
      selectedPoint.setState('hover');
    });
  });

  const tooltipContainer = chart.tooltip?.container;
  if (showHoverTooltips && tooltipContainer) {
    tooltipContainer.classList.remove('invisible');
  }
};

const drawTooltipOnPointX = (
  chart: FlareChart,
  xValue: number | string,
  onLinkClick: (url: string) => void,
) => {
  const tooltipContainer = chart?.tooltip?.container;

  if (!chart || !tooltipContainer) {
    return;
  }

  const clonedTooltipContainer = tooltipContainer.cloneNode(true) as HTMLElement;
  clonedTooltipContainer.classList.remove(
    'highcharts-tooltip-container', // use our class instead
    'invisible',
  );
  clonedTooltipContainer.classList.add(FLARE_STICKY_TOOLTIP_CONTAINER_CLASS);

  const linkedLabels = clonedTooltipContainer.querySelectorAll<HTMLSpanElement>('span[id]');

  linkedLabels.forEach((label) => {
    const url = label.getAttribute('id');
    label.style.cursor = 'pointer';
    if (label && url) {
      // turn it into a link
      label.onclick = () => onLinkClick(url);
    }
  });

  // Highcharts 13 wraps useHTML in a div (inside foreignObject) instead of a span.
  const tooltipCloseButtonParent =
    clonedTooltipContainer.querySelector('.highcharts-tooltip table')?.parentElement ??
    clonedTooltipContainer.querySelector('.highcharts-tooltip foreignObject div') ??
    clonedTooltipContainer.querySelector('.highcharts-tooltip > div, .highcharts-tooltip > span');

  if (tooltipCloseButtonParent) {
    tooltipCloseButtonParent.classList.add('relative');

    const closeButton = document.createElement('button');
    closeButton.className =
      'absolute top-1 right-1 flex h-6 w-6 cursor-pointer items-center justify-center rounded border border-transparent bg-transparent p-0 outline-inherit pointer-events-auto hover:bg-white/20 active:bg-white/30';
    closeButton.onclick = () => {
      stickyTooltipRemove(chart, true);
    };
    const closeButtonIcon = document.createElement('img');
    closeButtonIcon.src = closeIcon;
    closeButtonIcon.alt = t`Close`;
    closeButtonIcon.className = 'h-4 w-4 brightness-0 invert';
    closeButton.append(closeButtonIcon);
    tooltipCloseButtonParent.appendChild(closeButton);
  }

  // Insert the sticky tooltip in the scrollable main content container. `body` is not scrollable.
  (document.getElementById('pageRoot') ?? document.body).appendChild(clonedTooltipContainer);

  // save tooltip object
  chart.stickyTooltip = {
    xValue,
    element: clonedTooltipContainer,
  };

  tooltipContainer.classList.add('invisible');

  chart.hoverPoints?.forEach((hoverPoint, index) => {
    hoverPoint.select(true, index > 0);
  });
};

const stickyTooltipAdd = (point: Point | null, onLinkClick: (url: string) => void) => {
  const chart = point?.series.chart as FlareChart;
  if (!point || !chart) {
    return;
  }

  if (chart.stickyTooltip) {
    const isSamePoint = chart.stickyTooltip.xValue === point.x;
    stickyTooltipRemove(chart, isSamePoint);
    if (isSamePoint) {
      chart.hoverPoints?.forEach((hoverPoint) => {
        hoverPoint.setState('hover');
      });
      // don't redraw another sticky tooltip if the user clicked on the same x position twice.
      // Effectively makes this a toggle.
      return;
    }
  }

  drawTooltipOnPointX(chart, point.x, onLinkClick);
};

const buildTooltipOptions = (
  options: Highcharts.Options,
  parentProps: SanitizedFlareProps,
  props: TooltipProps,
  onLinkClick: (url: string) => void,
) => {
  const { colors } = parentProps;
  const {
    shared,
    rowLabelFormat,
    rowValueFormat,
    rowValueLink,
    rowSecondaryValueFormat = null,
    rowContent = null,
    unstable_hasRouterContext = true,
    titleFormat = null,
    positioner,
    showTotalRow = false,
    reversed = false,
    stickyOnClick = true,
  } = props;

  // Avoid using `this` context variable which arrow functions cannot utilize. The `positioner`
  // function should be an arrow function, therefore pass `this` as first param.
  // Chart→#pageRoot offset is applied in highcharts-tooltip-extension's updatePosition wrap.
  const defaultTooltipPositioner: DefaultPositionerCallbackFunction | undefined = positioner
    ? function (labelWidth, labelHeight, point) {
        return positioner(this, labelWidth, labelHeight, point);
      }
    : undefined;

  const config: Highcharts.Options = {
    ...options,
    tooltip: {
      enabled: true,
      // Width of the useHTML root is handled in tailwind.css for Highcharts 13 foreignObject.
      useHTML: true,
      shared,
      outside: true,
      followPointer: false,
      positioner: defaultTooltipPositioner,
      style: {
        pointerEvent: 'none',
        zIndex: 1099,
      },
      backgroundColor: 'none',
      borderColor: 'none',
      borderRadius: 0,
      borderWidth: 0,
      shadow: false,
      formatter: shared
        ? (function (this: TooltipPoint) {
            // In the case of shared or split tooltips, all points are available in `this.points`.
            // Some marks don't have `this.points` on Tooltip (Donut). We'll recreate those Tooltip
            // point objects here if that's the case.
            const points = isSharedPoint(this)
              ? this.points
              : (this.series?.points ?? []).map(
                  (point) => ({ ...this, ...point, key: point.name }) as TooltipPoint,
                );
            const dataPoints = reversed ? points.reverse() : points;

            // Tooltip showing all data point values
            return renderToStaticMarkup(
              <table className={tooltipTableClass}>
                {titleFormat && (
                  <thead>
                    <TooltipTitle>{titleFormat(this)}</TooltipTitle>
                  </thead>
                )}
                {rowValueFormat && (
                  <tbody>
                    {dataPoints
                      ?.filter((item) => item.visible)
                      .map((item, index) => (
                        <TooltipRow
                          key={index}
                          name={
                            rowLabelFormat ? rowLabelFormat(item) + '' : (item.series?.name ?? '')
                          }
                          value={rowValueFormat(item)}
                          valueLink={rowValueLink?.(item)}
                          unstable_hasRouterContext={unstable_hasRouterContext}
                          secondaryValue={rowSecondaryValueFormat?.(item)}
                          color={(item.color as string) || colors[item.colorIndex ?? 0]}
                        />
                      ))}
                    {showTotalRow && (
                      <TooltipRow
                        name={t`Total`}
                        value={rowValueFormat?.({
                          category: '',
                          colorIndex: -1,
                          color: undefined,
                          series: undefined,
                          key: 'total',
                          percentage: -1,
                          x: 0,
                          y: dataPoints?.reduce<number>((acc, item) => {
                            // if the series is disabled, the point won't be visible.
                            if (item.visible) {
                              return acc + (item?.y ?? 0);
                            }
                            return acc;
                          }, 0),
                          index: -1,
                          name: '',
                          selected: false,
                          visible: false,
                        })}
                        valueLink={rowValueLink?.({
                          category: '',
                          colorIndex: -1,
                          color: undefined,
                          series: undefined,
                          key: 'total',
                          percentage: -1,
                          x: 0,
                          index: -1,
                          name: '',
                          selected: false,
                          visible: false,
                        })}
                        unstable_hasRouterContext={unstable_hasRouterContext}
                      />
                    )}
                  </tbody>
                )}
                {rowContent && rowContent(dataPoints)}
              </table>,
            );
          } as TooltipFormatterCallbackFunction)
        : (function (this: TooltipPoint) {
            // Tooltip showing only the hovered data point's value
            return renderToStaticMarkup(
              <table className={tooltipTableClass}>
                {titleFormat && (
                  <thead>
                    <TooltipTitle>{titleFormat(this)}</TooltipTitle>
                  </thead>
                )}
                {rowValueFormat && (
                  <tbody>
                    <TooltipRow
                      name={rowLabelFormat ? rowLabelFormat(this) + '' : (this.series?.name ?? '')}
                      value={rowValueFormat(this)}
                      valueLink={rowValueLink?.(this)}
                      unstable_hasRouterContext={unstable_hasRouterContext}
                      secondaryValue={rowSecondaryValueFormat?.(this)}
                      color={(this.color as string) || colors[this.colorIndex ?? 0]}
                    />
                  </tbody>
                )}
                {rowContent && rowContent([this])}
              </table>,
            );
          } as TooltipFormatterCallbackFunction),
    },
  };

  if (stickyOnClick) {
    config.plotOptions = {
      ...config.plotOptions,
      series: {
        ...config.plotOptions?.series,
        cursor: 'pointer',
        point: {
          ...config.plotOptions?.series?.point,
          events: {
            ...config.plotOptions?.series?.point?.events,
            click: function (event) {
              if (options.plotOptions?.series?.point?.events?.click) {
                options.plotOptions?.series?.point?.events?.click.call(this, event);
              }

              stickyTooltipAdd(this, onLinkClick);
            },
          },
        },
      },
    };

    // Don't add this if we're dealing with a donut chart. Donut charts shouldn't click background.
    config.chart = {
      ...options.chart,
      className: STICKY_TOOLTIP_CHART_CLASS,
      events: {
        ...options.chart?.events,
        click: function (event) {
          if (options.chart?.events?.click) {
            options.chart.events.click.call(this, event);
          }

          if (this.hoverPoint) {
            stickyTooltipAdd(this.hoverPoint, onLinkClick);
          } else {
            stickyTooltipRemove(this as FlareChart, true);
          }
        },
      },
    };
  }

  return config;
};

const Tooltip = (props: TooltipProps) => {
  const navigate = useInstanceNavigate();
  const { id, registerChild, chart } = useFlareContext();
  const stickyOnClick = props.stickyOnClick == null || props.stickyOnClick;

  const handleNavigateToLink = useCallback(
    (url: string) => {
      if (url.startsWith('http')) {
        Object.assign(document.createElement('a'), {
          target: '_blank',
          rel: 'noopener noreferrer',
          href: url,
        }).click();
      } else {
        navigate(url);
      }
    },
    [navigate],
  );

  props = useMemo(() => {
    return {
      ...props,
      stickyOnClick,
    };
  }, [props, stickyOnClick]);

  useOnClickOutside(chart?.container, (event) => {
    if (chart?.stickyTooltip && !chart.stickyTooltip.element.contains(event.target as Node)) {
      stickyTooltipRemove(chart as FlareChart, true);
    }
  });

  useEffect(() => {
    registerChild(id, (options: Highcharts.Options, parentProps: SanitizedFlareProps) =>
      buildTooltipOptions(options, parentProps, props, handleNavigateToLink),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, handleNavigateToLink]);

  useEffect(() => {
    return () => {
      stickyTooltipRemove(chart as FlareChart);
      document
        .querySelectorAll(`.${FLARE_STICKY_TOOLTIP_CONTAINER_CLASS}`)
        .forEach((el) => el.remove());
    };
  }, [chart]);

  return null;
};

export default Tooltip;
